// Pagina gestione integrazioni esterne — lista completa con card cliccabili
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Puzzle, RefreshCw, TestTube } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Badge } from '../../components/ui/Badge'
import api from '../../lib/api'

import { IntegrationCard, type IntegrationStatus } from './components/IntegrationCard'
import { XAtlasConfig } from './components/XAtlasConfig'
import { ERPConfig } from './components/ERPConfig'
import { WhatsAppConfig } from './components/WhatsAppConfig'
import { CalendarConfig } from './components/CalendarConfig'
import { FattureInCloudConfig } from './components/FattureInCloudConfig'
import { FEQConfig } from './components/FEQConfig'
import { FCMConfig } from './components/FCMConfig'
import { IoTConfig } from './components/IoTConfig'
import { HRConfig } from './components/HRConfig'

// ---------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------

type CategoryFilter = 'all' | IntegrationStatus['category']

// ---------------------------------------------------------------
// IntegrationsPage
// ---------------------------------------------------------------

export function IntegrationsPage() {
  const qc = useQueryClient()
  const [configuring, setConfiguring] = useState<IntegrationStatus | null>(null)
  const [testingName, setTestingName] = useState<string | null>(null)
  const [togglingName, setTogglingName] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')

  // Carica lista integrazioni
  const { data: integrations = [], isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const res = await api.get<{ integrations: IntegrationStatus[] }>('/integrations')
      return res.data.integrations
    },
    refetchInterval: 60_000, // Aggiorna ogni minuto
  })

  // Mutation toggle enable/disable
  const toggleMutation = useMutation({
    mutationFn: async ({ name, enabled }: { name: string; enabled: boolean }) => {
      await api.put(`/integrations/${name}/config`, { enabled })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      setTogglingName(null)
    },
  })

  // Test all
  const testAllMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ results: IntegrationStatus[] }>('/integrations/test-all')
      return res.data.results
    },
    onSuccess: (results) => {
      // Aggiorna la cache con i risultati del test
      qc.setQueryData(['integrations'], results)
    },
  })

  // Filtra per categoria
  const filteredIntegrations = categoryFilter === 'all'
    ? integrations
    : integrations.filter((i) => i.category === categoryFilter)

  // Conta per categoria
  const configuredCount = integrations.filter((i) => i.configured).length
  const enabledCount = integrations.filter((i) => i.enabled).length

  // Gestione apertura modal configurazione
  const handleConfigure = (name: string) => {
    const integration = integrations.find((i) => i.name === name)
    if (integration) setConfiguring(integration)
  }

  // Toggle singola integrazione
  const handleToggle = (name: string, enabled: boolean) => {
    setTogglingName(name)
    toggleMutation.mutate({ name, enabled })
  }

  // Test singola integrazione
  const handleTest = async (name: string) => {
    setTestingName(name)
    try {
      const res = await api.post<{ ok: boolean; message: string; latencyMs?: number }>(
        `/integrations/${name}/test`,
      )
      // Aggiorna il testResult nella cache
      qc.setQueryData(['integrations'], (old: IntegrationStatus[] | undefined) =>
        old?.map((i) => i.name === name ? { ...i, testResult: res.data } : i)
      )
    } finally {
      setTestingName(null)
    }
  }

  // Render pannello configurazione per ogni integrazione
  const renderConfigPanel = (integration: IntegrationStatus) => {
    const meta = integration as IntegrationStatus & { metadata?: Record<string, unknown> }
    const currentMeta = meta.metadata

    const commonProps = {
      currentMetadata: currentMeta,
      onSaved: () => setConfiguring(null),
    }

    switch (integration.name) {
      case 'xatlas':
        return <XAtlasConfig {...commonProps} />
      case 'sap':
        return <ERPConfig integrationName="sap" displayName="SAP" {...commonProps} />
      case 'zucchetti':
        return <ERPConfig integrationName="zucchetti" displayName="Zucchetti" {...commonProps} />
      case 'teamsystem':
        return <ERPConfig integrationName="teamsystem" displayName="TeamSystem" {...commonProps} />
      case 'whatsapp':
        return <WhatsAppConfig {...commonProps} />
      case 'calendar':
        return <CalendarConfig {...commonProps} />
      case 'fattureincloud':
        return <FattureInCloudConfig {...commonProps} />
      case 'feq':
        return <FEQConfig {...commonProps} />
      case 'fcm':
        return <FCMConfig {...commonProps} />
      case 'iot':
        return <IoTConfig {...commonProps} />
      case 'hr':
        return <HRConfig {...commonProps} />
      default:
        return (
          <p className="text-sm text-[var(--fc-foreground-muted)]">
            Configurazione non disponibile per questa integrazione.
          </p>
        )
    }
  }

  const categoryOptions: Array<{ value: CategoryFilter; label: string }> = [
    { value: 'all', label: 'Tutte' },
    { value: 'erp', label: 'ERP' },
    { value: 'hr', label: 'HR' },
    { value: 'messaging', label: 'Messaggistica' },
    { value: 'calendar', label: 'Calendario' },
    { value: 'billing', label: 'Fatturazione' },
    { value: 'iot', label: 'IoT' },
    { value: 'signing', label: 'Firma' },
    { value: 'notifications', label: 'Notifiche' },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integrazioni"
        subtitle={`${configuredCount} configurate su ${integrations.length} — ${enabledCount} abilitate`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={
              testAllMutation.isPending
                ? <RefreshCw className="h-4 w-4 animate-spin" />
                : <TestTube className="h-4 w-4" />
            }
            onClick={() => testAllMutation.mutate()}
            isLoading={testAllMutation.isPending}
          >
            Testa tutte
          </Button>
        }
      />

      {/* Filtri categoria */}
      <div className="flex flex-wrap gap-2">
        {categoryOptions.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setCategoryFilter(value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              categoryFilter === value
                ? 'bg-[var(--fc-primary)] text-white'
                : 'bg-[var(--fc-gray-100)] text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-gray-200)]'
            }`}
          >
            {label}
            {value !== 'all' && (
              <span className="ml-1 opacity-60">
                ({integrations.filter((i) => i.category === value).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lista integrazioni */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-[var(--fc-gray-200)]" />
          ))}
        </div>
      ) : filteredIntegrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-[var(--fc-foreground-muted)]">
          <Puzzle className="h-12 w-12 mb-3 opacity-30" />
          <p className="text-sm">Nessuna integrazione trovata per questa categoria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filteredIntegrations.map((integration) => (
            <IntegrationCard
              key={integration.name}
              integration={integration}
              onConfigure={handleConfigure}
              onToggle={handleToggle}
              onTest={handleTest}
              isTesting={testingName === integration.name}
              isToggling={togglingName === integration.name}
            />
          ))}
        </div>
      )}

      {/* Modal configurazione */}
      <Modal
        open={Boolean(configuring)}
        onClose={() => setConfiguring(null)}
        title={`Configura ${configuring?.displayName ?? ''}`}
        size="md"
      >
        {configuring && renderConfigPanel(configuring)}
      </Modal>
    </div>
  )
}
